/**
 * Calculator Context Store.
 *
 * Global state for opening the calculator with context-aware pre-filled
 * values. Any component can call `openCalc()` with a mode and seed data
 * to launch the calculator in the right mode with the right numbers.
 */

import { create } from "zustand";

export type CalcMode =
  | "standard"
  | "margin"
  | "markup"
  | "profit"
  | "shipping"
  | "commission"
  | "roi"
  | "tax"
  | "converter";

export interface CalcContext {
  mode: CalcMode;
  /** Pre-fill values keyed by field name within the mode's form. */
  prefill: Record<string, string>;
  /** Label describing where the calc was triggered from. */
  source?: string;
  /** Callback to apply the calculator result back to the triggering field. */
  onApply?: (value: number) => void;
}

interface CalculatorState {
  isOpen: boolean;
  context: CalcContext | null;
  /** Open the calculator, optionally with a mode and pre-filled values. */
  openCalc: (ctx?: CalcContext) => void;
  /** Close the calculator. */
  closeCalc: () => void;
  /** Toggle without context (header button / keyboard shortcut). */
  toggleCalc: () => void;
  /** Clear any context (after it's been consumed by the calc component). */
  clearContext: () => void;
}

export const useCalculatorStore = create<CalculatorState>((set, get) => ({
  isOpen: false,
  context: null,

  openCalc: (ctx) => set({ isOpen: true, context: ctx || null }),

  closeCalc: () => set({ isOpen: false, context: null }),

  toggleCalc: () => {
    const current = get().isOpen;
    set({ isOpen: !current, context: current ? null : get().context });
  },

  clearContext: () => set({ context: null }),
}));

/**
 * Convenience functions for opening the calculator in specific modes
 * with pre-filled values from the current page context.
 */
export const calc = {
  /** Open margin calculator with cost pre-filled. */
  margin: (cost: number, source?: string) =>
    useCalculatorStore.getState().openCalc({
      mode: "margin",
      prefill: { cost: cost.toFixed(2) },
      source,
    }),

  /** Open markup calculator with cost pre-filled. */
  markup: (cost: number, source?: string) =>
    useCalculatorStore.getState().openCalc({
      mode: "markup",
      prefill: { cost: cost.toFixed(2) },
      source,
    }),

  /** Open profit calculator with sale price and purchase cost pre-filled. */
  profit: (
    opts: {
      salePrice?: number;
      purchaseCost?: number;
      reconCost?: number;
      shippingCost?: number;
    },
    source?: string,
  ) =>
    useCalculatorStore.getState().openCalc({
      mode: "profit",
      prefill: {
        ...(opts.salePrice != null && { salePrice: opts.salePrice.toFixed(2) }),
        ...(opts.purchaseCost != null && {
          purchaseCost: opts.purchaseCost.toFixed(2),
        }),
        ...(opts.reconCost != null && { reconCost: opts.reconCost.toFixed(2) }),
        ...(opts.shippingCost != null && {
          shippingCost: opts.shippingCost.toFixed(2),
        }),
      },
      source,
    }),

  /** Open commission calculator with sale amount pre-filled. */
  commission: (saleAmount: number, rate?: number, source?: string) =>
    useCalculatorStore.getState().openCalc({
      mode: "commission",
      prefill: {
        saleAmount: saleAmount.toFixed(2),
        ...(rate != null && { rate: (rate * 100).toFixed(2) }),
      },
      source,
    }),

  /** Open ROI calculator with invested amount pre-filled. */
  roi: (invested: number, returned?: number, source?: string) =>
    useCalculatorStore.getState().openCalc({
      mode: "roi",
      prefill: {
        invested: invested.toFixed(2),
        ...(returned != null && { returned: returned.toFixed(2) }),
      },
      source,
    }),

  /** Open tax calculator with subtotal pre-filled. */
  tax: (subtotal: number, rate?: number, source?: string) =>
    useCalculatorStore.getState().openCalc({
      mode: "tax",
      prefill: {
        subtotal: subtotal.toFixed(2),
        ...(rate != null && { taxRate: rate.toFixed(2) }),
      },
      source,
    }),

  /** Open shipping splitter with total shipping and item count. */
  shipping: (totalShipping: number, itemCount?: number, source?: string) =>
    useCalculatorStore.getState().openCalc({
      mode: "shipping",
      prefill: {
        totalShipping: totalShipping.toFixed(2),
        ...(itemCount != null && { items: String(itemCount) }),
      },
      source,
    }),

  /** Open standard calculator. */
  standard: () =>
    useCalculatorStore.getState().openCalc({ mode: "standard", prefill: {} }),

  /** Open converter with optional category pre-selected. */
  converter: (category?: string) =>
    useCalculatorStore.getState().openCalc({
      mode: "converter",
      prefill: category ? { category } : {},
    }),
};
